/**
 * Grand Quack Auto — vehicle system.
 *
 * Handles E-key enter/exit for vehicles and driving controls (WASD + brake).
 * When driving, the duck is hidden and the camera follows the active vehicle.
 * Wheel meshes are plain children of the car group at hardcoded local
 * positions — they travel with the chassis (which is synced from cannon by
 * physicsSystem). The one visual tweak in render() is steering the front
 * wheels of the active vehicle around local Y.
 *
 * See research.md §3 "Vehicle physics with cannon-es RaycastVehicle".
 */

import { DUCK, VEHICLE } from '../config';
import type { System, VehicleRef, World } from '../types';
import { hideCarHint, showCarHint } from '../ui/carHint';
import { startEngine, stopEngine, updateEngineSound } from '../utils/audio';
import { carIsStuck, carOverlapsBuilding, resetCarPose, resetStuckTracking, updateStuckCheck } from './vehicleRescue';
import * as CANNON from 'cannon-es';

// Auto-unstick state.
let stuckTimer = 0;

let currentSteer = 0;

// Session counter — used by statsScreen for the run summary.
let totalHijacked = 0;
/** Total cars the player has entered this session. */
export function getTotalHijacked(): number { return totalHijacked; }

function findNearestCar(world: World): VehicleRef | null {
  let best: VehicleRef | null = null;
  let bestDist: number = VEHICLE.enterRadius;
  for (const v of world.vehicles) {
    const d = world.duck.position.distanceTo(v.group.position);
    if (d < bestDist) { bestDist = d; best = v; }
  }
  return best;
}

function enterCar(world: World, car: VehicleRef): void {
  // If car is wedged in a building, teleport to spawn instead of just Y-lift.
  resetCarPose(car, carOverlapsBuilding(car));
  resetStuckTracking(car.chassisBody.position, world.time.elapsed);
  currentSteer = 0;
  totalHijacked++;
  world.playerMode = 'driving';
  world.activeVehicle = car;
  world.duck.visible = false;
  // Park the duck body so it doesn't collide with the car or get dragged.
  const duckBody = world.physics?.duckBody;
  if (duckBody) {
    duckBody.velocity.set(0, 0, 0);
    duckBody.sleep();
  }
  startEngine();
}

function exitCar(world: World): void {
  if (!world.activeVehicle) return;
  const car = world.activeVehicle;

  // Position duck next to the car.
  world.duck.position.copy(car.group.position);
  world.duck.position.x -= 2;
  world.duck.position.y = 0;

  // Zero out car forces.
  const v = car.vehicle;
  for (let i = 0; i < v.wheelInfos.length; i++) {
    v.applyEngineForce(0, i);
    v.setBrake(VEHICLE.brakeForce, i);
  }
  currentSteer = 0;

  world.playerMode = 'foot';
  world.activeVehicle = null;
  world.duck.visible = true;
  // Snap the duck body to the new spawn position and wake it.
  const duckBody = world.physics?.duckBody;
  if (duckBody) {
    const radius = duckBody.shapes[0] instanceof CANNON.Sphere
      ? duckBody.shapes[0].radius
      : DUCK.bodyRadius;
    duckBody.position.set(world.duck.position.x, radius, world.duck.position.z);
    duckBody.velocity.set(0, 0, 0);
    duckBody.wakeUp();
  }
  stopEngine();
}

export const vehicleSystem: System = {
  name: 'vehicle',

  fixedUpdate(dt: number, world: World) {
    // ── Auto-rescue parked cars in a bad pose ─────────────────────────
    // Runs on any car that isn't actively being driven. A stuck car is
    // teleported back to its original spawn — necessary because a Y-lift
    // alone can't extract a chassis that's wedged horizontally inside a
    // building collider.
    for (const car of world.vehicles) {
      if (car === world.activeVehicle) continue;
      if (carIsStuck(car)) resetCarPose(car, true);
    }

    // ── Car proximity hint ────────────────────────────────────────────
    if (world.playerMode === 'foot') {
      const near = findNearestCar(world);
      if (near) showCarHint(); else hideCarHint();
    } else {
      hideCarHint();
    }

    // ── Enter / Exit ──────────────────────────────────────────────────
    if (world.input.enterVehicle) {
      if (world.playerMode === 'foot') {
        const car = findNearestCar(world);
        if (car) { hideCarHint(); enterCar(world, car); }
      } else {
        exitCar(world);
      }
    }

    // ── Driving controls ──────────────────────────────────────────────
    if (world.playerMode !== 'driving' || !world.activeVehicle) return;
    const v = world.activeVehicle.vehicle;
    const { move, quack } = world.input;

    // Throttle: W/S → engine force on rear wheels (indices 2, 3).
    // Negated because car mesh forward is +Z but cannon-es drives -Z.
    const force = -move.y * VEHICLE.engineForce;
    v.applyEngineForce(force, 2);
    v.applyEngineForce(force, 3);

    // Steering: A/D → lerped steer on front wheels (indices 0, 1).
    const targetSteer = move.x * VEHICLE.maxSteer;
    currentSteer += (targetSteer - currentSteer) * VEHICLE.steerLerp;
    v.setSteeringValue(currentSteer, 0);
    v.setSteeringValue(currentSteer, 1);

    // Brake: Space (reused from quack when driving).
    const brake = quack ? VEHICLE.brakeForce : 0;
    for (let i = 0; i < v.wheelInfos.length; i++) {
      v.setBrake(brake, i);
    }

    // Remove engine force from front wheels (front-wheel drive off).
    v.applyEngineForce(0, 0);

    // Update engine sound with current speed.
    const vel = world.activeVehicle.chassisBody.velocity;
    const speed = Math.sqrt(vel.x * vel.x + vel.y * vel.y + vel.z * vel.z);
    updateEngineSound(speed);

    // Auto-flip recovery: if car is upside down, reset it upright.
    const body = world.activeVehicle.chassisBody;
    const up = new CANNON.Vec3(0, 1, 0);
    const carUp = new CANNON.Vec3();
    body.quaternion.vmult(up, carUp);
    if (carUp.y < 0.3) {
      // Car is flipped — reset rotation and lift up.
      stuckTimer += dt;
      if (stuckTimer > 1.0) {
        body.quaternion.set(0, 0, 0, 1);
        body.angularVelocity.set(0, 0, 0);
        body.position.y += 1.5;
        stuckTimer = 0;
      }
    } else {
      stuckTimer = 0;
    }

    // Displacement-based stuck detection: if pressing gas but the car hasn't
    // moved meaningfully for 2 seconds, teleport to spawn. More reliable than
    // speed checks — wheels spinning against a wall can report velocity.
    const hasInput = Math.abs(move.y) > 0.1;
    if (updateStuckCheck(body.position, world.time.elapsed, hasInput)) {
      resetCarPose(world.activeVehicle, true);
      resetStuckTracking(body.position, world.time.elapsed);
      currentSteer = 0;
    }
    v.applyEngineForce(0, 1);
  },

  render(_dt: number, _alpha: number, world: World) {
    // Wheels are decorative children of each car group, fixed at their
    // chassis-local positions set in car.ts. The car group itself is
    // transform-synced from the chassis body by physicsSystem, so wheels
    // move/rotate correctly with the car. We only animate the front wheels
    // of the ACTIVE vehicle around their local Y to show steering.
    const active = world.activeVehicle;
    if (!active) return;
    const front = [active.wheelMeshes[0], active.wheelMeshes[1]];
    for (const wm of front) {
      if (wm) wm.rotation.y = currentSteer;
    }
  },
};
